import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, FileText, Radio } from 'lucide-react';
import { useImportRun, useImportRuns } from '../../api/ingestion';
import type { ImportDataset, ImportRun, ImportStatus } from '../../api/types';

const DATASET_LABEL: Record<ImportDataset, string> = {
  PRODUCTS: 'Produits',
  ORDERS: 'Ordres de fabrication',
  OPERATIONS: 'Opérations',
  COMPONENTS: 'Composants',
  STOCK: 'Stock',
  TOOLING: 'Outillage',
};

function StatusBadge({ status }: { status: ImportStatus }) {
  const map: Record<ImportStatus, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    SUCCESS: { label: 'Réussi', cls: 'bg-green-100 text-green-800', Icon: CheckCircle2 },
    PARTIAL: { label: 'Partiel', cls: 'bg-amber-100 text-amber-800', Icon: AlertTriangle },
    FAILED: { label: 'Échec', cls: 'bg-red-100 text-red-800', Icon: XCircle },
    RUNNING: { label: 'En cours', cls: 'bg-blue-100 text-blue-800', Icon: Clock },
  };
  const { label, cls, Icon } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon size={13} /> {label}
    </span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Ecran de suivi des imports ERP (lot 2). */
export default function ImportsView() {
  const { data: runs, isLoading, error, refetch, isFetching } = useImportRuns();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <h1 className="text-xl font-bold">Suivi des imports ERP</h1>
          <div className="flex items-center gap-4">
            <button onClick={() => refetch()} className="flex items-center gap-2 text-blue-100 hover:text-white">
              <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Actualiser
            </button>
            <Link to="/" className="flex items-center gap-2 text-blue-100 hover:text-white">
              <Home size={18} /> Accueil
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {isLoading && <p className="text-gray-500">Chargement du journal…</p>}
        {error && <p className="text-red-600">Impossible de charger le journal des imports.</p>}

        {runs && runs.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Aucun import pour l'instant. Les dépôts ERP (n8n ou fichier) apparaîtront ici.
          </div>
        )}

        {runs && runs.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Dataset</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Créés</th>
                  <th className="px-4 py-3 text-right">Mis à jour</th>
                  <th className="px-4 py-3 text-right">Rejetés</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {runs.map((run) => (
                  <RunRow key={run.id} run={run} onDetail={() => setSelectedId(run.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedId != null && <RejectsModal runId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function RunRow({ run, onDetail }: { run: ImportRun; onDetail: () => void }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(run.startedAt)}</td>
      <td className="px-4 py-3 font-medium text-gray-900">{DATASET_LABEL[run.dataset]}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-gray-600">
          {run.source === 'FILE' ? <FileText size={14} /> : <Radio size={14} />}
          {run.source === 'FILE' ? (run.fileName ?? 'fichier') : 'API'}
        </span>
      </td>
      <td className="px-4 py-3"><StatusBadge status={run.status} /></td>
      <td className="px-4 py-3 text-right text-green-700">{run.createdCount}</td>
      <td className="px-4 py-3 text-right text-blue-700">{run.updatedCount}</td>
      <td className="px-4 py-3 text-right">
        {run.rejectedCount > 0 ? <span className="text-red-700 font-medium">{run.rejectedCount}</span> : <span className="text-gray-400">0</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {run.rejectedCount > 0 && (
          <button onClick={onDetail} className="text-blue-600 hover:text-blue-800 text-sm">
            Voir les rejets
          </button>
        )}
      </td>
    </tr>
  );
}

function RejectsModal({ runId, onClose }: { runId: number; onClose: () => void }) {
  const { data: run, isLoading } = useImportRun(runId);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">Rejets de l'import #{runId}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>
        <div className="p-6">
          {isLoading && <p className="text-gray-500">Chargement…</p>}
          {run && run.rejects.length === 0 && <p className="text-gray-500">Aucun rejet.</p>}
          {run && run.rejects.length > 0 && (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 w-16">Ligne</th>
                  <th className="px-3 py-2">Motif</th>
                  <th className="px-3 py-2">Donnée brute</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {run.rejects.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="px-3 py-2 text-gray-500">{r.lineNumber ?? '—'}</td>
                    <td className="px-3 py-2 text-red-700">{r.reason}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 break-all">{JSON.stringify(r.rawData)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
